import { HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { SINGLE_QUOTES_REGEX } from '@common/constants';
import { BooleanOperator, ComparisonOperator } from '@common/enums';
import { ContactDetails, ContactFolderODataItem, ContactODataItem } from '@common/models';
import { ODataPagedResult } from '@common/odata';
import { SharedOdataService } from '@common/services';
import { AbilityService } from '@common/services/auth/ability.service';
import { ContactsTag, ContactsTagsMap } from '@common/services/smart-search/contacts';
import { FilterOptions } from '@common/types';
import { getOdataTop } from '@common/utils';
import { containsWrapper, filterByWords, getFilterByContains } from '@common/utils/functions/search';
import { I18NextPipe } from 'angular-i18next';
import { I18_NAMESPACE_APPS_UI } from 'i18n';
import { SmartSearchModel, SmartSearchModelField } from 'mbs-ui-kit';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class ContactsService extends SharedOdataService<ContactODataItem> {
  private readonly ability = inject(AbilityService);
  private readonly i18nPipe = inject(I18NextPipe);
  private readonly odataMethods = this.odataFactory.CreateService<any>('');

  protected readonly moduleContacts = I18_NAMESPACE_APPS_UI.contacts;

  readonly #firstName = ContactsTagsMap.get(ContactsTag.FirstName);
  readonly #lastName = ContactsTagsMap.get(ContactsTag.LastName);
  readonly #primaryEmail = ContactsTagsMap.get(ContactsTag.PrimaryEmail);
  readonly #phoneNumber = ContactsTagsMap.get(ContactsTag.PhoneNumber);

  protected readonly isOffice = this.ability.isOffice;

  public accessor groupId: string;

  constructor() {
    super('ContactItems');
  }

  public getContactsItemsByGroupId(id: string): Observable<ODataPagedResult<ContactODataItem>> {
    const newOdata = this.odataFactory.CreateService<ContactODataItem>(`/ContactGroups(${id})/ContactItems`);

    return newOdata
      .Query()
      .OrderBy(this.state.orderBy)
      .Filter(this.filter.length > 0 ? this.filter.concat(' and IsLast eq true') : 'IsLast eq true')
      .Top(this.state.pageSize)
      .Skip(this.state.pageSize * (this.state.page - 1))
      .ExecWithCount();
  }

  public getContactVersions(groupId: string, providerId: string, version: number): Observable<ODataPagedResult<ContactODataItem>> {
    const newOdata = this.odataFactory.CreateService<ContactODataItem>(`/ContactGroups(${groupId})/ContactItems`);

    return newOdata.Query().OrderBy('Version desc').Filter(`(ProviderId eq '${providerId}' and Version ne ${version})`).ExecWithCount();
  }

  public getContactDetails(id: string): Observable<ContactDetails> {
    return this.odata.CustomFunction(id, 'GetDetails').pipe(map((res: HttpResponse<ContactDetails>) => res.body));
  }

  public getContactsFolders(
    userId: string,
    options?: { inbox?: boolean; search?: string; skip?: number }
  ): Observable<ODataPagedResult<ContactFolderODataItem>> {
    const { inbox, search, skip } = options || {};
    const defaultFolderName = this.isOffice()
      ? this.i18nPipe.transform(this.moduleContacts + ':contacts', { format: 'capitalize' })
      : this.i18nPipe.transform(this.moduleContacts + ':myContacts', { format: 'capitalize' });
    const filter = inbox ? `Name ${ComparisonOperator.Eq} '${defaultFolderName}'` : `Name ${ComparisonOperator.Ne} '${defaultFolderName}'`;

    return this.odataFactory
      .CreateService<ContactFolderODataItem>(`/BackupUsers(${userId})/ContactGroups`)
      .Query()
      .OrderBy(inbox ? null : 'Name asc')
      .Top(getOdataTop(search || inbox))
      .Skip(skip)
      .Filter(search ? containsWrapper('Name', search) : filter)
      .ExecWithCount();
  }

  public restoreContacts(CategoryId: string, Ids: string[]): Observable<any> {
    return this.odataMethods.CustomCollectionAction('RestoreContacts', { CategoryId, Ids });
  }

  public deleteContacts(data: { Ids: string[]; Password: string; WithAllRevisions: boolean }): Observable<any> {
    return this.odataMethods.CustomCollectionAction('DeleteContactItems', data);
  }

  public restoreContactsGroup(CategoryId: string): Observable<any> {
    return this.odataMethods.CustomCollectionAction('RestoreContactsGroup', { CategoryId });
  }

  public deleteContactsGroup(data: { Ids: string[]; Password: string }): Observable<any> {
    return this.odataMethods.CustomCollectionAction('DeleteContactGroups', data);
  }

  public restoreAllContacts(UserId: string): Observable<any> {
    return this.odataMethods.CustomCollectionAction('RestoreAllContacts', { UserId });
  }

  updateFilter(searchObj: SmartSearchModel): void {
    const filter: string[] = [];

    if (searchObj[this.#firstName.tag]) {
      const options: FilterOptions = {
        model: searchObj[this.#firstName.tag] as SmartSearchModelField[],
        prop: this.#firstName.prop
      };

      filter.push(this.convertFiltersToStr(this.#firstName.prop, getFilterByContains(options)));
    }

    if (searchObj[this.#lastName.tag]) {
      const options: FilterOptions = {
        model: searchObj[this.#lastName.tag] as SmartSearchModelField[],
        prop: this.#lastName.prop
      };

      filter.push(this.convertFiltersToStr(this.#lastName.prop, getFilterByContains(options)));
    }

    if (searchObj[this.#primaryEmail.tag]) {
      const options: FilterOptions = {
        model: searchObj[this.#primaryEmail.tag] as SmartSearchModelField[],
        prop: this.#primaryEmail.prop
      };

      filter.push(this.convertFiltersToStr(this.#primaryEmail.prop, getFilterByContains(options)));
    }

    if (searchObj[this.#phoneNumber.tag]) {
      const options: FilterOptions = {
        model: searchObj[this.#phoneNumber.tag] as SmartSearchModelField[],
        prop: this.#phoneNumber.prop
      };

      filter.push(this.convertFiltersToStr(this.#phoneNumber.prop, getFilterByContains(options)));
    }

    if (searchObj.words?.filter(Boolean)) {
      const term = filterByWords(searchObj).replace(SINGLE_QUOTES_REGEX, '');
      const keys = [this.#firstName.prop, this.#lastName.prop, this.#primaryEmail.prop, this.#phoneNumber.prop];
      const values = keys.map((k) => `(${k} ${ComparisonOperator.Ne} null ${BooleanOperator.And} ${containsWrapper(k, term)})`);

      filter.push('('.concat(values.join(` ${BooleanOperator.Or} `), ')'));
    }

    this.filter = filter.length > 0 ? filter.join(` ${BooleanOperator.And} `) : '';
  }

  convertFiltersToStr = (prop: string, filters: string) => `(${prop} ${ComparisonOperator.Ne} null ${BooleanOperator.And} ${filters})`;
}
